import defaultElumiaImage from "../assests/default-elumia.webp";

const DELIVERY_FALLBACK = {
  label: "Delivery In 4hr",
  detail: "Shown at checkout",
  timeline: null,
  isPlaceholder: true,
};

export const DEFAULT_PRODUCT_IMAGE = defaultElumiaImage;

const DELIVERY_KEYS = [
  "delivery_time",
  "deliveryTime",
  "delivery_eta",
  "deliveryEta",
  "delivery_estimate",
  "deliveryEstimate",
  "eta",
  "sla",
];

const DELIVERY_ATTRIBUTE_KEYS = [
  "delivery time",
  "delivery eta",
  "delivery estimate",
  "delivery",
  "eta",
  "shipping promise",
  "delivery promise",
];

export const cleanNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const normalized = String(value).replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getDisplayPrice = (product) =>
  cleanNumber(
    product?.selling_price ??
      product?.seller_price ??
      product?.finalPrice ??
      product?.price ??
      product?.priceNumber,
  );

export const hasOfferPricing = (product) => {
  const offerId = cleanNumber(product?.offer_id ?? product?.offerId);
  const originalPrice = cleanNumber(
    product?.original_price ?? product?.originalPrice,
  );
  const offerPrice = cleanNumber(product?.offer_price ?? product?.offerPrice);

  return offerId > 0 && originalPrice > 0 && offerPrice > 0;
};

export const getOriginalDisplayPrice = (product) =>
  hasOfferPricing(product)
    ? cleanNumber(product?.original_price ?? product?.originalPrice)
    : getDisplayPrice(product);

export const getDisplayDiscountAmount = (product) => {
  if (hasOfferPricing(product)) {
    const directAmount = cleanNumber(
      product?.offer_discount_amount ?? product?.offerDiscountAmount,
    );

    if (directAmount > 0) {
      return directAmount;
    }

    return Math.max(
      0,
      getOriginalDisplayPrice(product) -
        cleanNumber(product?.offer_price ?? product?.offerPrice),
    );
  }

  const originalPrice = getDisplayPrice(product);
  const discountPercent = cleanNumber(product?.discount);
  return discountPercent > 0 ? (originalPrice * discountPercent) / 100 : 0;
};

export const getFinalDisplayPrice = (product) => {
  if (hasOfferPricing(product)) {
    return cleanNumber(product?.offer_price ?? product?.offerPrice);
  }

  const originalPrice = getDisplayPrice(product);
  return Math.max(0, originalPrice - getDisplayDiscountAmount(product));
};

export const getDisplayPricing = (product) => ({
  hasOffer: hasOfferPricing(product),
  originalPrice: getOriginalDisplayPrice(product),
  discountAmount: getDisplayDiscountAmount(product),
  finalPrice: getFinalDisplayPrice(product),
  discountPercent: hasOfferPricing(product) ? 0 : cleanNumber(product?.discount),
  offerId: cleanNumber(product?.offer_id ?? product?.offerId) || null,
  offerTitle: String(product?.offer_title ?? product?.offerTitle ?? "").trim(),
  offerMessage: String(product?.offer_message ?? product?.offerMessage ?? "").trim(),
});

const normalizeImageString = (value) => {
  const trimmed = String(value || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "");

  if (!trimmed) return "";

  const normalizedLower = trimmed.toLowerCase();
  if (
    normalizedLower === "null" ||
    normalizedLower === "undefined" ||
    normalizedLower === "nan" ||
    normalizedLower === "placeholder.svg" ||
    normalizedLower.endsWith("/placeholder.svg") ||
    normalizedLower.includes("via.placeholder.com")
  ) {
    return "";
  }

  return trimmed;
};

const collectProductImages = (candidate, results) => {
  if (!candidate) return;

  if (Array.isArray(candidate)) {
    candidate.forEach((entry) => collectProductImages(entry, results));
    return;
  }

  if (typeof candidate === "object") {
    const objectCandidates = [
      candidate.image_url,
      candidate.product_image,
      candidate.product_image_key,
      candidate.image,
      candidate.imageUrl,
      candidate.singleImage,
      candidate.allImages,
      candidate.thumbnails,
      candidate.src,
      candidate.url,
      candidate.image_urls,
      candidate.product_snapshot?.image_url,
      candidate.product_snapshot?.product_image,
      candidate.product_snapshot?.image_urls,
    ];

    objectCandidates.forEach((entry) => collectProductImages(entry, results));
    return;
  }

  const normalized = normalizeImageString(candidate);

  if (!normalized) return;

  if (normalized.startsWith("[") && normalized.endsWith("]")) {
    try {
      const parsed = JSON.parse(normalized);
      collectProductImages(parsed, results);
      return;
    } catch {
      return;
    }
  }

  if (normalized.includes(",")) {
    normalized
      .split(",")
      .map((entry) => normalizeImageString(entry))
      .filter(Boolean)
      .forEach((entry) => collectProductImages(entry, results));
    return;
  }

  if (!results.includes(normalized)) {
    results.push(normalized);
  }
};

export const getProductImageList = (product) => {
  const results = [];
  collectProductImages(product, results);
  return results;
};

export const getProductImage = (product) => {
  if (!product) return DEFAULT_PRODUCT_IMAGE;

  const resolvedImage = getProductImageList(product)[0];

  return resolvedImage || DEFAULT_PRODUCT_IMAGE;
};

export const withDefaultProductImage = (image) =>
  getProductImage(image) || DEFAULT_PRODUCT_IMAGE;

export const getProductPricing = (product) => {
  const pricing = getDisplayPricing(product);
  const originalPrice = Math.round(
    pricing.hasOffer
      ? pricing.originalPrice
      : cleanNumber(product?.oldPrice ?? product?.old_price ?? product?.price),
  );
  const discount = pricing.hasOffer
    ? Math.round(pricing.discountAmount)
    : cleanNumber(product?.discount);
  const discountedPrice = Math.round(pricing.finalPrice);

  return {
    originalPrice,
    discountedPrice,
    discount,
  };
};

export const getProductStockQuantity = (product) => {
  if (!product) return 0;

  const candidates = [
    product?.product_stock_quantity,
    product?.stock_quantity,
    product?.stock,
    product?.inventory,
    product?.totalStock,
    product?.available_quantity,
    product?.availableQty,
    product?.Available_Qty,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) {
      return Math.max(0, parsed);
    }
  }

  return 0;
};

export const isProductOutOfStock = (product) => {
  if (!product) return true;
  if (product?.is_out_of_stock === true) return true;
  if (product?.is_out_of_stock === false) return false;

  const stock = getProductStockQuantity(product);
  const normalizedStatus = String(product?.status || product?.product_status || "")
    .trim()
    .toLowerCase();

  if (normalizedStatus === "inactive") return true;
  if (stock > 0) return false;

  return normalizedStatus === "out_of_stock" || stock <= 0;
};

const normalizeDeliveryText = (value) => {
  if (!value) return null;

  return String(value)
    .trim()
    .replace(/\s+/g, " ")
    .replace(/minutes?/gi, "min")
    .replace(/hours?/gi, "hr")
    .replace(/mins?/gi, "min")
    .replace(/hrs?/gi, "hr");
};

const getAttributeMatch = (attributes = {}) => {
  if (!attributes || typeof attributes !== "object") return null;

  const normalizedEntries = Object.entries(attributes).map(([key, value]) => [
    String(key).trim().toLowerCase(),
    value,
  ]);

  const match = normalizedEntries.find(([key]) =>
    DELIVERY_ATTRIBUTE_KEYS.includes(key),
  );

  return match ? match[1] : null;
};

export const getDeliveryPromise = (product) => {
  if (!product) return DELIVERY_FALLBACK;

  const directValue = DELIVERY_KEYS.map((key) => product?.[key]).find(Boolean);
  const attributeValue = getAttributeMatch(product?.attributes);
  const rawValue = directValue || attributeValue;
  const timeline = normalizeDeliveryText(rawValue);

  if (!timeline) return DELIVERY_FALLBACK;

  return {
    label: "Delivery",
    detail: timeline,
    timeline,
    isPlaceholder: false,
  };
};

const normalizeCollection = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const dedupeProducts = (products) => {
  const seen = new Set();

  return products.filter((product) => {
    const key = product?.id ?? product?.product_id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const flattenProducts = ({ productdata, Homeproductdata }) => {
  const categoryProducts = normalizeCollection(Homeproductdata).flatMap(
    (category) => (Array.isArray(category?.products) ? category.products : []),
  );
  const directProducts = normalizeCollection(productdata);

  return dedupeProducts([...categoryProducts, ...directProducts]);
};

const sortByNumericField = (products, field) =>
  [...products].sort((a, b) => cleanNumber(b?.[field]) - cleanNumber(a?.[field]));

export const buildHomepageSections = (products) => {
  const safeProducts = Array.isArray(products) ? products : [];

  const trending = safeProducts.slice(0, 10);
  const deals = sortByNumericField(
    safeProducts.filter((product) => cleanNumber(product?.discount) > 0),
    "discount",
  ).slice(0, 10);
  const bestSelling = safeProducts
    .filter(
      (product) =>
        product?.featured === true ||
        Number(product?.featured) === 1 ||
        Number(product?.featured) > 0,
    )
    .slice(0, 10);
  const topRated = sortByNumericField(
    safeProducts.filter((product) => cleanNumber(product?.rating) > 0),
    "rating",
  ).slice(0, 10);

  return [
    trending.length
      ? {
          key: "trending",
          title: "Trending",
          subtitle: "Popular picks from the current catalog",
          products: trending,
        }
      : null,
    deals.length
      ? {
          key: "deals",
          title: "Deals",
          subtitle: "Products with active offers right now",
          products: deals,
        }
      : null,
    bestSelling.length
      ? {
          key: "best-selling",
          title: "Best-selling",
          subtitle: "Featured products getting the most attention",
          products: bestSelling,
        }
      : null,
    topRated.length
      ? {
          key: "top-rated",
          title: "Top-rated",
          subtitle: "Shown only when rating data is available",
          products: topRated,
        }
      : null,
  ].filter(Boolean);
};

export const resolveBannerHref = (banner) => {
  const rawLink =
    banner?.link_url || banner?.link || banner?.redirect_url || banner?.url;

  if (rawLink) return String(rawLink).trim();
  if (banner?.product_id) return `/product/${banner.product_id}`;
  if (banner?.category_id) return `/viewall/${banner.category_id}`;
  if (banner?.brand_id) return `/brandsproduct/${banner.brand_id}`;

  return "";
};

export const isExternalLink = (href) => /^https?:\/\//i.test(href);
